import React, { Fragment, useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { replace, push } from "connected-react-router";
import { func, number, shape, arrayOf } from "prop-types";
import { Link, useParams, useLocation } from "react-router-dom";
import { isEmpty } from "lodash";
import { selectors as sessionSelectors } from "reducers/session";
import { actions as accountsActions, selectors as accountsSelectors } from "reducers/accounts";
import { selectors as loginSelectors } from "reducers/login";
import { actions as menuActions } from "reducers/drawer";
import { selectors as formSelectors } from "reducers/form";

import AccountAction from "pages/accounts/_components/AccountAction";
import AccountTransfers from "pages/accounts/_components/AccountTransfers";

import Information from "pages/accounts/_components/Information";
import MoreFilters from "pages/accounts/_components/MoreFilters";
import Options from "pages/accounts/_components/Options";
import Movements from "pages/accounts/Movements";
import Button from "pages/_components/Button";
import Notification from "pages/_components/Notification";
import PageLoading from "pages/_components/PageLoading";
import ProductDetail from "pages/_components/ProductDetail";
import I18n from "pages/_components/I18n";
import * as i18nUtils from "util/i18n";
import Dropdown from "pages/_components/Dropdown";

import * as configUtil from "util/config";
import { actions as settingsActions } from "reducers/settings";

import { bool, string } from "yup";
import CustomButton from "pages/_components/CustomButton/CustomButton";
import Drawer from "pages/_components/Drawer/Drawer";
import CustomText from "pages/_components/CustomText/CustomText";
import RadioButtonGroup from "pages/forms/_components/_fields/_commons/RadioButtonGroup";
import gaEventTracker, { events } from "util/gaEventTracker";
import { isIOS, isMobile } from "react-device-detect";
import CustomHeadTitle from "./_components/CustomHeadTitle";
import StatementList from "./_components/CustomStatement";
import QRImage from "./_components/QRImage";
import { CHECKING_ACCOUNT } from "./AgreementRequestPage/constants";

const Details = (props) => {
    const prevAccountsRef = useRef(props.accounts);
    const [selectedFilter, setSelectedFilter] = useState(null);
    const [qrIsVisible, setQrIsVisible] = useState(false);
    const [filtersAreVisible, setFiltersAreVisible] = useState(false);
    const [balance, setBalance] = useState(true);
    const [currentTab, setCurrentTab] = useState(0);
    const [radioTab, setRadioTab] = useState("lastMonth");
    const { id: matchParamsId } = useParams();
    const { pathname: url } = useLocation();

    useEffect(() => {
        const { dispatch, filters, prevRoute } = props;
        const { prevPath = "" } = prevRoute;
        const isMovementPage = prevPath.split("/").includes("movement");
        const isPending = prevPath.split("/").includes("pending");
        if (!isMovementPage) {
            dispatch(accountsActions.clearMovementsFilters());
        }

        const bibankAccountsEnabled = configUtil.getBoolean("bibank.accounts.type.enabled");
        if (bibankAccountsEnabled) {
            dispatch(accountsActions.listAllAccounts());
        } else {
            dispatch(accountsActions.listAccounts());
        }
        dispatch(accountsActions.readAccount(matchParamsId, true));
        if (isPending) {
            setSelectedFilter("pending");
        } else {
            setSelectedFilter("lastMonth");
        }

        const _filters = isMovementPage ? { ...filters } : null;

        dispatch(accountsActions.details(matchParamsId, _filters));
    }, []);

    useEffect(() => {
        const { dispatch, accounts } = props;

        if (accounts.length > 0) {
            const selectedAccount = accounts?.find((account) => account.productId === matchParamsId);
            dispatch(accountsActions.filterBalances(selectedAccount));
        }
        prevAccountsRef.current = accounts;
    }, [props.accounts]);

    const handleClickDownload = (format) => {
        const { dispatch, account, filters } = props;
        if (selectedFilter === "lastMonth") {
            dispatch(accountsActions.downloadMovements(account.idProduct, format));
        } else {
            dispatch(
                accountsActions.downloadPendingMovements(
                    {
                        idAccount: account.idProduct,
                        dateFrom: filters.dateFrom,
                        dateTo: filters.dateTo,
                    },
                    format,
                ),
            );
        }
    };

    const handleClick = () => {
        const { dispatch } = props;
        dispatch(menuActions.menuShow());
    };

    const handleFilterRadioClick = (id) => {
        const { dispatch, filters, account } = props;
        setRadioTab(id);
        setSelectedFilter(id);

        switch (id) {
            case "lastMonth":
                dispatch(accountsActions.cleanAccountMovements());
                dispatch(accountsActions.setFilterMovements({ ...filters, parentFilter: "lastMonth" }));
                dispatch(accountsActions.details(matchParamsId, { ...filters, parentFilter: "lastMonth" }));
                break;
            case "pending":
                dispatch(accountsActions.cleanAccountMovements());
                dispatch(accountsActions.setFilterMovements({ ...filters, parentFilter: "pending" }));
                dispatch(
                    accountsActions.details(matchParamsId, {
                        ...filters,
                        cbu: account?.uniformBankCode,
                        parentFilter: "pending",
                    }),
                );
                break;
            default:
                break;
        }
    };

    const toolbarChildren = () => {
        const { account, isDesktop, activeEnvironmentForms } = props;

        const forms = activeEnvironmentForms.reduce(
            (map, form) => ({
                ...map,
                [form.idForm]: form,
            }),
            {},
        );

        return [
            <AccountTransfers key="account.transfers" account={account} isDesktop={isDesktop} />,
            account.productType === "CC" && account.permissions["accounts.requestCheckbook"] && (
                <AccountAction
                    isDesktop={isDesktop}
                    key="requestCheckbook"
                    labelKey="accounts.requestCheckbook"
                    to="/form/requestCheckbook"
                />
            ),
            forms.requestOverdraft && (
                <AccountAction
                    isDesktop={isDesktop}
                    key="requestOverdraft"
                    labelKey="accounts.requestOverdraft"
                    to="/"
                />
            ),
            isDesktop && (
                <AccountAction
                    isDesktop={isDesktop}
                    key="statements"
                    labelKey="accounts.statements"
                    to={`/accounts/${matchParamsId}/statements`}
                />
            ),
            isDesktop && account.permissions.accountFund && (
                <AccountAction
                    isDesktop={isDesktop}
                    key="fund"
                    labelKey="accounts.fund"
                    to={`/form/accountFund?creditAccount=${matchParamsId}`}
                />
            ),
        ];
    };

    const handleClickShowQr = () => {
        const { dispatch, qrCode } = props;
        if (!qrCode) {
            dispatch(accountsActions.readAccountQr(matchParamsId));
        }
        setQrIsVisible(true);
    };

    const handleClickHideQr = () => {
        setQrIsVisible(false);
    };

    const handleClickShowFilters = () => {
        setFiltersAreVisible(true);
    };

    const handleClickHideFilters = () => {
        setFiltersAreVisible(false);
    };

    const restartTab = () => {
        const regex = /[a-zA-z0-9]+/;
        const path = url.match(regex)[0];
        sessionStorage.setItem(`tab-${path}`, 0);
    };

    const handleBack = () => {
        const { dispatch } = props;
        restartTab();
        dispatch(accountsActions.resetFilters());
        dispatch(replace(`/accounts/`));
    };

    const handleSetCurrentTab = (index) => {
        setCurrentTab(index);
    };

    const {
        totalCount,
        account,
        channels,
        fetching,
        fetchingDownload,
        fetchingMovements,
        dispatch,
        isDesktop,
        activeRegion,
        qrCode,
        selectedAccountBalances,
        isFetchingExport,
        fetchingAlias,
        ...rest
    } = props;

    const setBalanceFalse = () => {
        setBalance(false);
    };

    const setBalanceTrue = () => {
        setBalance(true);
    };

    const submitFilter = (e) => {
        rest.isSubmitting(e);
        setFiltersAreVisible(false);
    };

    const filterRadio = [
        <div className={isIOS ? "d-flex justify-between" : null}>
            <RadioButtonGroup
                className={isIOS ? "d-flex flex-column" : null}
                inLineControl
                optionClassName="needsclick"
                value={selectedFilter}
                selectorId="accounts.filters"
                renderAs="radio"
                onChange={handleFilterRadioClick}
                options={[
                    {
                        id: "lastMonth",
                        label: i18nUtils.get("accounts.movements.filters.lastMovements"),
                    },
                    {
                        id: "pending",
                        label: i18nUtils.get("accounts.movements.filters.pending"),
                    },
                ]}
            />
        </div>,
    ];

    const qrImage = qrCode ? `data:image/png;base64,${qrCode}` : "";

    const renderMenuOption = () => {
        const options = [
            {
                /* eslint-disable no-dupe-keys */
                text: i18nUtils.get("accounts.agreement.close"),
                func: () => {
                    gaEventTracker.triggerEvent(events.cuentas_baja());
                },
                func: () => {
                    dispatch(settingsActions.setDefaultOtherProduct("DELETE_ACCOUNT"));
                    dispatch(push(`/loans/otrosProductos`));
                },
                /* eslint-disable no-dupe-keys */
            },
        ];
        if (account.productType === CHECKING_ACCOUNT) {
            options.splice(0, 0, {
                text: i18nUtils.get("accounts.agreement.view"),
                func: () => dispatch(push(`/accounts/${account.idProduct}/agreements`)),
            });
        }
        if (account.productType === CHECKING_ACCOUNT) {
            options.splice(1, 0, {
                text: i18nUtils.get("accounts.agreement.open"),
                func: () => dispatch(push(`/accounts/${account.idProduct}/agreements/request`)),
            });
        }
        return (
            <>
                {isDesktop ? (
                    <>
                        {currentTab === 0 && (
                            <div className="toolbar-item view-agreements" key="exportList">
                                <Dropdown
                                    image="images/figma/icon/download.svg"
                                    label="global.download"
                                    buttonClass="btn btn-outline"
                                    fetching={isFetchingExport}
                                    pullRight>
                                    <Button
                                        onClick={() => handleClickDownload("pdf")}
                                        label="global.download.pdf"
                                        className="dropdown__item-btn"
                                        bsStyle="link"
                                    />
                                    <Button
                                        onClick={() => handleClickDownload("xls")}
                                        label="global.download.xls"
                                        className="dropdown__item-btn"
                                        bsStyle="link"
                                    />
                                </Dropdown>
                            </div>
                        )}
                        <div className="toolbar-item historical-balances">
                            <Link
                                className="btn btn-outline btn-block"
                                to={`/accounts/${account.idProduct}/historicalBalances`}>
                                <I18n
                                    id="accounts.accountBanner.historicalBalances"
                                    componentProps={!isDesktop && { className: "visually-hidden" }}
                                />
                            </Link>
                        </div>
                        <div className="toolbar-item more-button">
                            <CustomButton
                                className="toolbar-item"
                                icon="images/figma/icon/menu-mobile.svg"
                                pullRight
                                widthXl
                                options={options}
                            />
                        </div>
                    </>
                ) : (
                    <div className="toolbar-item commands-mobile">
                        <Button
                            className="toolbar-btn view-options"
                            onClick={handleClick}
                            image={isIOS ? "images/figma/icon/menu-ios-white.svg" : "images/figma/icon/menu-ios.svg"}
                            label="view more options"
                            block={false}
                            bsStyle="link"
                        />
                    </div>
                )}
            </>
        );
    };

    return (
        <PageLoading loading={fetching || fetchingDownload || fetchingAlias || !account || isEmpty(account)}>
            {!fetching && !isEmpty(account) && (
                <Fragment>
                    <Notification scopeToShow="account/details" />
                    <ProductDetail>
                        <ProductDetail.CustomHead
                            className="movement-details"
                            onBack={handleBack}
                            renderMenuOption={renderMenuOption}>
                            <CustomHeadTitle account={account} isDesktop={isDesktop} />
                        </ProductDetail.CustomHead>
                        <ProductDetail.Body
                            filtersClass={isIOS ? "d-flex justify-between" : null}
                            specialFilter
                            handleClickShowQr={handleClickShowQr}
                            isDesktop={isDesktop}
                            dispatch={dispatch}
                            productId={matchParamsId}
                            filters={filterRadio}
                            filtersKeyLabel="accounts.movements"
                            persistentTabs
                            setCurrentTab={handleSetCurrentTab}
                            moreFilters={
                                <div>
                                    <CustomButton
                                        className="btn-link"
                                        func={handleClickShowFilters}
                                        icon="images/figma/icon/filter-primary.svg"
                                        text="accounts.movements.filters"
                                        textType="I18N"
                                    />
                                </div>
                            }
                            moreFiltersClosedKeyLabel="accounts.movements.filters.more"
                            moreFiltersOpenedKeyLabel="accounts.movements.filters.less"
                            account={account}
                            selectedAccountBalances={selectedAccountBalances}>
                            <Movements
                                accountId={account.idProduct}
                                accountCurrency={account.currency}
                                isDesktop={isDesktop}
                                keyLabel="accounts.movements"
                                showBalance={balance}
                                radioTab={radioTab}
                            />
                            <StatementList accountId={account.idProduct} />

                            {!isDesktop && (
                                <Information
                                    account={account}
                                    keyLabel="accounts.information"
                                    dispatch={dispatch}
                                    activeRegion={activeRegion}
                                />
                            )}
                        </ProductDetail.Body>
                    </ProductDetail>

                    {!isDesktop && (
                        <Options>
                            <AccountAction
                                isDesktop={isDesktop}
                                labelKey="accounts.accountBanner.agreements"
                                to={`/accounts/${account.idProduct}/agreements`}
                            />
                            <AccountAction
                                isDesktop={isDesktop}
                                labelKey="accounts.accountBanner.historicalBalances"
                                to={`/accounts/${account.idProduct}/historicalBalances`}
                            />
                            <AccountAction
                                isDesktop={isDesktop}
                                labelKey="accounts.alias.setAlias"
                                to={`/accounts/${account.idProduct}/setAlias`}
                            />
                            {toolbarChildren()}
                        </Options>
                    )}
                    <Drawer
                        className="qr-slider"
                        enableCloseButton
                        openSidebar={qrIsVisible}
                        isFullHeight
                        toggleSidebar={handleClickHideQr}
                        {...props}>
                        <QRImage qrImage={qrImage} toggleQr={handleClickHideQr} isDesktop={isDesktop} />
                    </Drawer>
                    <Drawer
                        className="discounts-echeq-filter-drawer"
                        enableCloseButton
                        openSidebar={filtersAreVisible}
                        toggleSidebar={handleClickHideFilters}
                        {...props}
                        isFullHeight={isIOS}>
                        {!isIOS && !isMobile ? (
                            <div className="container full-width">
                                <h2 className="title-content">
                                    <CustomText text="accounts.movements.filters.header" textType="I18N" />
                                </h2>
                                <div className="row height-100">
                                    <MoreFilters
                                        parentSelectedFilter={selectedFilter}
                                        channels={channels}
                                        currency={account.currency}
                                        dispatch={dispatch}
                                        isDesktop={isDesktop}
                                        isSubmitting={submitFilter}
                                        productId={matchParamsId}
                                        setBalanceFalse={setBalanceFalse}
                                        setBalanceTrue={setBalanceTrue}
                                        handleClickHideFilters={handleClickHideFilters}
                                    />
                                </div>
                            </div>
                        ) : (
                            <div className="container full-width">
                                <div className="mb-4 f-weight-b">
                                    <I18n id="accounts.movements.filters.header" component="h2" />
                                </div>
                                <div className="text-left">
                                    <MoreFilters
                                        parentSelectedFilter={selectedFilter}
                                        channels={channels}
                                        currency={account.currency}
                                        dispatch={dispatch}
                                        isDesktop={isDesktop}
                                        isSubmitting={submitFilter}
                                        productId={matchParamsId}
                                        setBalanceFalse={setBalanceFalse}
                                        setBalanceTrue={setBalanceTrue}
                                        handleClickHideFilters={handleClickHideFilters}
                                    />
                                </div>
                            </div>
                        )}
                    </Drawer>
                </Fragment>
            )}
        </PageLoading>
    );
};

Details.propTypes = {
    account: shape({
        productType: string,
        idProduct: string,
        permissions: shape(),
        currency: string,
    }).isRequired,
    activeEnvironmentForms: shape({
        reduce: func,
    }).isRequired,
    channels: arrayOf().isRequired,
    qrCode: string.isRequired,
    fetching: bool.isRequired,
    fetchingDownload: func.isRequired,
    fetchingMovements: func.isRequired,
    fetchingQr: bool.isRequired,
    fetchingAlias: bool.isRequired,
    totalAccounts: number.isRequired,
    match: shape().isRequired,
    isDesktop: bool.isRequired,
    dispatch: func.isRequired,
    totalCount: number.isRequired,
    activeRegion: string.isRequired,
    selectedAccountBalances: shape({
        totalBalance: number,
        availableBalance: number,
        proyectedOverdraftBalance: number,
    }).isRequired,
    filters: shape({}).isRequired,
    isFetchingExport: bool.isRequired,
    find: func.isRequired,
    accounts: [].isRequired,
    prevRoute: shape({}).isRequired,
    history: func.isRequired,
};

const mapStateToProps = (state) => ({
    account: accountsSelectors.getSelectedAccount(state),
    totalAccounts: accountsSelectors.getTotalAccounts(state),
    channels: configUtil.getArray("core.enabledChannels"),
    qrCode: accountsSelectors.getQrCode(state),
    fetching: accountsSelectors.getFetching(state),
    fetchingDownload: accountsSelectors.getFetchingDownload(state),
    fetchingMovements: accountsSelectors.getFetchingMovements(state),
    fetchingQr: accountsSelectors.getFetchingQr(state),
    fetchingAlias: accountsSelectors.getFetchingAlias(state),
    activeEnvironmentForms: sessionSelectors.getActiveEnvironmentForms(state, "accounts"),
    totalCount: accountsSelectors.getTotalCount(state),
    activeRegion: loginSelectors.getRegion(state),
    selectedAccountBalances: accountsSelectors.getSelectedAccountBalances(state),
    filters: accountsSelectors.getFilters(state),
    accounts: accountsSelectors.getAccounts(state),
    prevRoute: formSelectors.getPrevRoute(state),
});

export default connect(mapStateToProps)(Details);
